/**
 * §H2 aoi — "no AOI → explainer + `Start drawing` (closes dock, activates draw). With AOI:
 * **Focus dataset** select … Compact geometry row … Then dataset-specific blocks; footer
 * buttons `Clip data to this area | Release clip`, `Statistics|Composition CSV`,
 * `Clear AOI`".
 *
 * **The insights run themselves.** Every other card is a form with a button; this one is a
 * question the reader has already asked by drawing the polygon, and the mockup answers it
 * the moment the card opens. So the run fires on `(aoi.id, layer_id)` — the AOI's *identity*
 * (`lib/aoi.ts`), not the array, so a re-render or a permalink restore is not a redraw — and
 * a repeat is free anyway, because that pair is exactly what §E7 hashes.
 *
 * **The panel switches on `kind`, not on the layer's `is_categorical`.** They agree today,
 * but the payload is the thing that was actually computed: a continuous block rendered from
 * a categorical answer would print `mean: —` four times and look like missing data.
 *
 * `Start drawing` reaches §F5.10's draw toggle through the DOM, as the mockup does
 * (`$('#btnDraw').click()`). That button owns the tool's exclusive-mode state and the
 * Leaflet handlers behind it; lifting that into the store to serve one cross-panel call
 * would put a second writer on a mode that must have exactly one.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  aoiRing,
  useToolRun,
  type AoiCategorical,
  type AoiContinuous,
  type AoiResult,
} from '../../../lib/analysis';
import { fetchConfig, queryKeys } from '../../../lib/api';
import {
  BarsV,
  ChartFrame,
  Donut,
  DonutLegend,
  Spark,
  axisTitle,
  chartH,
  chartW,
} from '../../../components/charts';
import { fmtKm2, fmtNum, fmtPct } from '../../../lib/format';
import { Icon } from '../../../lib/icons';
import { useAppStore } from '../../../lib/state';
import { FALLBACK_RAMP_STOPS, sampleRamp } from '../../../lib/symbology';
import { toast } from '../../../lib/toast';
import type { ToolProps } from '../AnalysisDock';
import { ToolFrame } from '../ToolFrame';
import { ResultBox, ResultNote } from '../ResultBox';
import { downloadCsv, toCsv } from '../csv';
import { startDrawing } from '../draw';
import { analysisRasters, firstAvailable, useAnalysisLayers } from '../pickers';

/** `tools/aoi.HISTOGRAM_BINS` — the count only matters until a payload names its own. */
export const HISTOGRAM_BINS = 8;

export function Aoi({ onBack }: ToolProps) {
  const { t } = useTranslation();
  const aoi = useAppStore((s) => s.aoi);
  const aoiClip = useAppStore((s) => s.aoiClip);
  const aoiFocus = useAppStore((s) => s.aoiFocus);
  const layersOn = useAppStore((s) => s.layersOn);
  const setAoi = useAppStore((s) => s.setAoi);
  const setAoiClip = useAppStore((s) => s.setAoiClip);
  const setAoiFocus = useAppStore((s) => s.setAoiFocus);
  const setAnalysisOpen = useAppStore((s) => s.setAnalysisOpen);
  const toggleLayer = useAppStore((s) => s.toggleLayer);

  const layers = useAnalysisLayers();
  const rasters = useMemo(() => analysisRasters(layers), [layers]);
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const run = useToolRun<AoiResult>('aoi');

  /**
   * §H2: "default = last active raster on map (note 'Defaulted to the layer currently on
   * your map')". `layersOn` is a Set with no order, so the *catalogue* order decides which
   * of two switched-on rasters is "the" one — the same rule `activeRasterOf` applies.
   */
  const onMap = rasters.filter((l) => layersOn.has(l.id));
  const mapRaster = onMap[onMap.length - 1]?.id ?? null;
  const layerId = firstAvailable(rasters, aoiFocus, mapRaster);
  const focus = rasters.find((l) => l.id === layerId) ?? null;
  const defaulted = Boolean(mapRaster && layerId === mapRaster && aoiFocus !== layerId);

  const ringKey = aoi?.id ?? null;
  const { run: fire } = run;
  useEffect(() => {
    if (!ringKey || !layerId || !aoi) return;
    fire({ layer_id: layerId, polygon: aoiRing(aoi.pts) });
    // The ring's *identity* and the layer are the whole of the question; `aoi.pts` itself
    // is a new array on every store write and would re-run on a pan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ringKey, layerId, fire]);

  const result = run.result;
  const continuous = result?.kind === 'continuous' ? (result as AoiResult & AoiContinuous) : null;
  const categorical = result?.kind === 'categorical' ? (result as AoiResult & AoiCategorical) : null;

  /**
   * §H2: "BarsV histogram (bins tinted from the layer ramp)". `sampleRamp` interpolates, so
   * ask it for one colour per bin the payload actually carries (`HISTOGRAM_BINS` is eight
   * server-side) rather than re-spreading five stops across eight bars, which gave bins
   * 2–3 and 5–6 the same colour and put a double step between 3 and 4.
   */
  const rampStops = (focus?.ramp && config?.ramps?.[focus.ramp]) || FALLBACK_RAMP_STOPS;
  const nBins = continuous?.histogram?.bins.length ?? HISTOGRAM_BINS;
  const binColors = useMemo(() => sampleRamp(rampStops, nBins), [rampStops, nBins]);

  const toggleClip = () => {
    const next = !aoiClip;
    setAoiClip(next);
    if (next && layerId && !layersOn.has(layerId)) toggleLayer(layerId, true);
    toast(next ? t('analysis.aoi.clipped', { layer: focus?.name ?? '' }) : t('tools.clipReleased'));
  };

  const exportCsv = () => {
    if (!result) return;
    const header = [t('analysis.csv.metric'), t('analysis.csv.value'), t('analysis.csv.source')];
    const rows: (readonly unknown[])[] = [
      [t('analysis.aoi.dataset'), result.layer.name, `${result.layer.dataset}@${result.layer.version}`],
      ['area_km2', result.geometry.area_km2, 'geodesic'],
      ['perimeter_km', result.geometry.perimeter_km, 'geodesic'],
    ];
    if (continuous) {
      for (const key of ['mean', 'min', 'max', 'std'] as const) {
        rows.push([key, continuous.stats[key], result.layer.id]);
      }
      rows.push(['pixels', continuous.stats.pixels, result.layer.id]);
      if (continuous.benchmark?.delta_pct !== null && continuous.benchmark) {
        rows.push(['vs_programme_mean_pct', continuous.benchmark.delta_pct, 'benchmark']);
      }
    }
    if (categorical) {
      for (const row of categorical.composition) {
        rows.push([`share_${row.label}`, row.pct, result.layer.id]);
      }
    }
    downloadCsv(`aoi_${result.layer.id}_insights.csv`, toCsv(header, rows));
  };

  if (!aoi) {
    return (
      <ToolFrame tool="aoi" onBack={onBack} phase="idle" progress={0} error={null}>
        <div className="note info">{t('analysis.aoi.explainer')}</div>
        <button
          type="button"
          className="btn primary ad-run"
          id="aoiDraw"
          onClick={() => {
            setAnalysisOpen(false);
            if (!startDrawing()) toast(t('analysis.aoi.drawUnavailable'));
          }}
        >
          <Icon name="draw" size={13} /> {t('analysis.aoi.startDrawing')}
        </button>
      </ToolFrame>
    );
  }

  return (
    <ToolFrame
      tool="aoi"
      onBack={onBack}
      phase={run.phase}
      progress={run.progress}
      error={run.error}
      cached={run.cached}
      result={
        result ? (
          <>
            {continuous ? (
              <>
                <ResultBox title={t('analysis.aoi.stats', { layer: result.layer.name })}>
                  <table className="mini">
                    <tbody>
                      <tr>
                        <td>{t('analysis.zonal.stat.mean')}</td>
                        <td className="num">
                          <b>{fmtNum(continuous.stats.mean, 2)}</b>
                        </td>
                        <td>{t('analysis.zonal.stat.std')}</td>
                        <td className="num">{fmtNum(continuous.stats.std, 2)}</td>
                      </tr>
                      <tr>
                        <td>{t('analysis.zonal.stat.min')}</td>
                        <td className="num">{fmtNum(continuous.stats.min, 2)}</td>
                        <td>{t('analysis.zonal.stat.max')}</td>
                        <td className="num">{fmtNum(continuous.stats.max, 2)}</td>
                      </tr>
                      <tr>
                        <td>{t('analysis.aoi.pixels')}</td>
                        <td className="num">{fmtNum(continuous.stats.pixels, 0)}</td>
                        <td>{t('analysis.aoi.resolution')}</td>
                        <td className="num">
                          {continuous.resolution_m ? `${Math.round(continuous.resolution_m)} m` : '-'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {continuous.benchmark && continuous.benchmark.delta_pct !== null ? (
                    <div
                      className={`result-pad ${continuous.benchmark.delta_pct >= 0 ? 'delta-up' : 'delta-down'}`}
                      data-role="benchmark"
                      style={{ fontSize: '11.6px' }}
                    >
                      {continuous.benchmark.delta_pct >= 0 ? '▲' : '▼'}{' '}
                      {t(
                        continuous.benchmark.delta_pct >= 0
                          ? 'analysis.aoi.aboveMean'
                          : 'analysis.aoi.belowMean',
                        { pct: fmtNum(Math.abs(continuous.benchmark.delta_pct), 0) },
                      )}
                    </div>
                  ) : null}
                </ResultBox>

                {continuous.histogram ? (
                  <ResultBox title={t('analysis.aoi.distribution')}>
                    <div className="result-pad">
                      <ChartFrame
                        title={t('analysis.aoi.distribution')}
                        subtitle={result.layer.name}
                        xLabel={axisTitle(t('analysis.axis.layerValue'), result.layer.unit)}
                        yLabel={t('analysis.axis.pixels')}
                        testId="aoi-histogram"
                      >
                        {(size) => (
                          <BarsV
                            labels={continuous.histogram!.bins.map((_, i) =>
                              fmtNum(
                                ((continuous.histogram?.edges[i] ?? 0) +
                                  (continuous.histogram?.edges[i + 1] ?? 0)) /
                                  2,
                                1,
                              ),
                            )}
                            values={continuous.histogram!.bins}
                            colors={binColors}
                            w={chartW(size, 290)}
                            h={chartH(size, 110)}
                            ariaLabel={t('analysis.aoi.distribution')}
                          />
                        )}
                      </ChartFrame>
                    </div>
                    {continuous.shares ? (
                      <table className="mini">
                        <tbody>
                          <tr data-role="shares">
                            {(['low', 'med', 'high'] as const).map((key) => (
                              <td key={key} style={{ textAlign: 'center' }}>
                                <b style={{ fontFamily: 'var(--mono)' }}>
                                  {fmtPct(continuous.shares?.[key] ?? 0, 0, false)}
                                </b>
                                <br />
                                <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                                  {t(`analysis.aoi.share.${key}`)}
                                </span>
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    ) : null}
                  </ResultBox>
                ) : null}

                {continuous.series?.length ? (
                  <ResultBox title={t('analysis.aoi.trend', { layer: result.layer.name })} pad>
                    <Spark
                      series={continuous.series.map((p) => p.mean)}
                      labels={continuous.series.map((p) => p.year)}
                      w={320}
                      h={68}
                      color="#C77B21"
                    />
                  </ResultBox>
                ) : null}
              </>
            ) : null}

            {categorical ? (
              <>
                <ResultBox title={t('analysis.aoi.composition', { layer: result.layer.name })}>
                  <div
                    className="result-pad"
                    style={{ display: 'flex', gap: 14, alignItems: 'center' }}
                  >
                    <ChartFrame
                      title={t('analysis.aoi.composition', { layer: result.layer.name })}
                      legend={
                        <DonutLegend
                          items={categorical.composition.map((c) => ({
                            label: c.label,
                            value: c.km2,
                            color: c.color,
                          }))}
                          fmt={1}
                          unit="km²"
                        />
                      }
                      testId="aoi-composition"
                    >
                      {(size) => (
                        <Donut
                          items={categorical.composition.map((c) => ({
                            label: c.label,
                            value: c.pct,
                            color: c.color,
                          }))}
                          size={size.expanded ? Math.round(Math.min(size.w, size.h) * 0.62) : 110}
                          centerLabel={fmtNum(result.geometry.area_km2, 0)}
                          centerSub="KM²"
                        />
                      )}
                    </ChartFrame>
                    <div className="hp-mini-legend">
                      {categorical.composition.map((c) => (
                        <span key={c.class}>
                          <i style={{ background: c.color, borderRadius: 3 }} />
                          {c.label} · <b>{fmtPct(c.pct, 0, false)}</b> ({fmtKm2(c.km2)})
                        </span>
                      ))}
                    </div>
                  </div>
                </ResultBox>

                {categorical.change_vs ? (
                  <ResultBox
                    title={t('analysis.aoi.changeVs', { year: categorical.change_vs.epoch ?? '' })}
                  >
                    <table className="mini">
                      <tbody>
                        {categorical.change_vs.rows.map((row) => (
                          <tr key={row.class}>
                            <td>
                              <span className="status-dot" style={{ background: row.color }} />
                              {row.label}
                            </td>
                            <td className={`num ${row.delta_pct >= 0 ? 'delta-up' : 'delta-down'}`}>
                              {row.delta_pct >= 0 ? '+' : ''}
                              {fmtNum(row.delta_pct, 1)}%
                            </td>
                            <td className="num" style={{ color: 'var(--ink-3)' }}>
                              {fmtKm2(Math.abs(row.km2))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <ResultNote>{t('analysis.aoi.changeNote')}</ResultNote>
                  </ResultBox>
                ) : null}
              </>
            ) : null}

            <div className="ad-actions">
              <button
                type="button"
                className={`btn ${aoiClip ? 'ghost' : 'primary'} sm`}
                id="aoiClipBtn"
                onClick={toggleClip}
              >
                <Icon name="swipe" size={12} /> {aoiClip ? t('analysis.aoi.release') : t('analysis.aoi.clip')}
              </button>
              <button type="button" className="btn ghost sm" id="aoiCsv" onClick={exportCsv}>
                <Icon name="download" size={12} />{' '}
                {categorical ? t('analysis.aoi.csvComposition') : t('analysis.aoi.csvStats')}
              </button>
              <button
                type="button"
                className="btn ghost sm"
                id="aoiClear"
                style={{ color: 'var(--restricted)' }}
                onClick={() => setAoi(null)}
              >
                {t('analysis.aoi.clear')}
              </button>
            </div>
            <ResultNote>{t('analysis.aoi.clipNote', { layer: result.layer.name })}</ResultNote>
          </>
        ) : null
      }
    >
      <label className="fld" htmlFor="aoiFocus">
        {t('analysis.aoi.focus')}{' '}
        <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>{t('analysis.aoi.focusHint')}</span>
      </label>
      <select
        className="std"
        id="aoiFocus"
        value={layerId}
        onChange={(e) => setAoiFocus(e.target.value)}
      >
        {rasters.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name} · {l.source_org}
          </option>
        ))}
        {rasters.length === 0 ? <option value="">{t('analysis.noLayerOption')}</option> : null}
      </select>
      {defaulted ? (
        <div className="tbl-note" data-note="defaulted">
          {t('analysis.aoi.defaulted')}
        </div>
      ) : null}
      {layerId && !layersOn.has(layerId) ? (
        <button
          type="button"
          className="btn ghost sm"
          id="aoiShow"
          style={{ marginTop: 6 }}
          onClick={() => toggleLayer(layerId, true)}
        >
          <Icon name="layers" size={12} /> {t('analysis.aoi.show')}
        </button>
      ) : null}

      <ResultBox title={t('analysis.aoi.geometry')}>
        <table className="mini">
          <tbody>
            <tr>
              <td>{t('analysis.aoi.area')}</td>
              <td className="num">
                <b>{fmtKm2(result?.geometry.area_km2 ?? aoi.areaKm2)}</b>
              </td>
              <td>{t('analysis.aoi.perimeter')}</td>
              <td className="num">
                {result ? `${fmtNum(result.geometry.perimeter_km, 0)} km` : '-'}
              </td>
              <td>{t('analysis.aoi.units')}</td>
              <td className="num">
                {result
                  ? t('analysis.aoi.unitsValue', {
                      regions: result.geometry.units_intersected.regions,
                      communes: result.geometry.units_intersected.communes,
                    })
                  : '-'}
              </td>
            </tr>
          </tbody>
        </table>
      </ResultBox>
    </ToolFrame>
  );
}

export default Aoi;
